import React, { useEffect, useRef } from 'react'
import V2Layout from '../components/V2Layout'
import useCompanyStats from '../components/useCompanyStats'
import '../versions/v2/story-timelines.css'

const NODES = [
    { yr: 2004, label: 'Founded', era: '2004–2005 · Auburn, CA', heading: 'Founded: Engineering & Design', body: "Launched in a 2,000 sq ft workshop, delivering engineering design work and the company's first launch vehicle hardware.", chips: ['2,000 sq ft', 'Auburn, CA'], programs: 'Falcon 1 · Merlin 1A · BE1 H₂O₂ engine', color: '#4A9ED6' },
    { yr: 2007, label: 'First CNC', era: '2007 · Auburn, CA', heading: 'First CNC Machining', body: 'Expanded to 3,000 sq ft and brought precision machining in-house with the first Mazak CNC lathe - still running today.', chips: ['3,000 sq ft', '1st Mazak Lathe'], programs: '', color: '#3DB990' },
    { yr: 2010, label: 'Autoflow Acq.', era: '2008–2010', heading: 'Autoflow Products Acquisition', body: 'Acquired Autoflow Products Co., adding flow switches to the portfolio, and expanded into maritime submarine regulators.', chips: ['Flow Switches', 'Maritime Systems'], programs: 'Falcon 9 · Dragon Cargo · Merlin 1D · New Shepard · BE3 · NASA Stennis', color: '#D4537E' },
    { yr: 2011, label: 'Dome Regs', era: '2011 · Auburn, CA', heading: 'Dome-Loaded Regulators', body: 'Added a second Mazak CNC lathe and launched the dome-loaded regulator product line for high-pressure applications.', chips: ['2nd Mazak', 'Dome Regulators'], programs: '', color: '#9590D0' },
    { yr: 2012, label: 'Cryo + Clean', era: '2012 · Auburn, CA', heading: 'Cryogenic Capability & Clean Room', body: 'Grew to 5,000 sq ft, established cryogenic butterfly valve production, and built a clean room.', chips: ['5,000 sq ft', 'Cryo Valves', 'Clean Room'], programs: 'Falcon Heavy · Crew Dragon · Starship · BE4 engine · Launcher One', color: '#D4A84B' },
    { yr: 2016, label: 'Rocklin', era: '2016 · Rocklin, CA', heading: 'Relocation to Rocklin', body: 'Outgrew Auburn and moved to a 15k sq ft facility in Rocklin, CA, adding a 4-axis Mazak mill.', chips: ['15k sq ft', 'Rocklin, CA', '4-axis Mill'], programs: 'Raptor engine · New Glenn · Dream Chaser · Firefly Alpha · RS1', color: '#E07B5A' },
    { yr: 2018, label: 'Swiss + 5-axis', era: '2017–2018', heading: 'Advanced Machining Investment', body: 'Added 5-axis milling, a Swiss-type screw machine, and robotic passivation for tighter tolerances and higher throughput.', chips: ['5-axis Mill', 'Swiss Machine', 'Robotic Passivation'], programs: '', color: '#7F77DD' },
    { yr: 2019, label: 'Lincoln Campus', era: '2019 · Lincoln, CA', heading: 'Lincoln Campus & Martin Testing Labs', body: "The company's largest expansion - 77k sq ft in Lincoln, CA - plus the acquisition of Martin Testing Labs.", chips: ['77k sq ft', 'Lincoln, CA', '+ Martin Testing'], programs: '', color: '#4A9ED6' },
    { yr: 2020, label: 'CNC Expansion', era: '2020 · Lincoln, CA', heading: 'Additional CNC Capacity', body: 'Added 7 additional CNC machines to meet growing production demand.', chips: ['+7 CNCs'], programs: '', color: '#4FB88A' },
    { yr: 2024, label: 'Expanded Production Capacity', era: '2020–Present', heading: 'Expanded Production Capacity', body: 'Added 23+ CNC machines including 3 Swiss-type machines; expanded clean room and test cell capacity at scale.', chips: ['+23 CNCs', '3 Swiss Machines', 'Expanded Clean Room'], programs: 'Crew Dragon · Starship/Raptor · New Glenn · Blue Moon · Vulcan · Neutron', color: '#5BBF5B' },
]

const ERAS = [
    { period: '2004 – 2006', desc: 'Engineering design · First launch vehicle hardware', tags: ['Falcon 1', 'Merlin 1A', 'BE1 H₂O₂ engine'] },
    { period: '2008 – 2010', desc: 'Launch vehicle valves, maritime regs, cryo & throttling', tags: ['Falcon 9', 'Dragon Cargo', 'Merlin 1D', 'New Shepard', 'BE3 engine', 'Maritime sub regs', 'NASA Stennis', 'NASA Marshall'] },
    { period: '2012 – 2015', desc: 'Cryo butterfly valves, large throttling, production ramp', tags: ['Falcon Heavy', 'Crew Dragon', 'Starship', 'BE4 engine', 'Launcher One'] },
    { period: '2016 – 2018', desc: 'Larger dome regs, updated clean room, Rocklin facility', tags: ['Raptor engine', 'New Glenn', 'Hadley engine', 'Dream Chaser', 'Firefly Alpha', 'RS1'] },
    { period: '2020 – Present', desc: 'Full production scale - cryo, ambient, relief, sleeve valves & regulators across the industry', tags: ['Crew Dragon', 'Starship / Raptor', 'BE3U engine', 'BE7 engine', 'Blue Moon Lander', 'XB-1', 'Terran 1', 'Terran R', 'Nova (Stoke)', 'Talon-A', 'Vulcan', 'Neutron', 'Haven-1', 'Winnebago-1', 'VIP aircraft completions'] },
]

function hexRgb(h) { return [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16)).join(',') }

const OurStoryV1Screen = () => {
    const { stats } = useCompanyStats()
    const stageRef = useRef(null)
    const outerRef = useRef(null)

    useEffect(() => {
        if (!stageRef.current) return
        const stage = stageRef.current
        stage.innerHTML = '<div class="stl-spine"></div>'

        const YS = 2003, YE = 2026, PL = 60, PR = 90
        const outer = outerRef.current
        const avail = outer.clientWidth - 80 // approximate padding
        const PPY = (avail - PL - PR) / (YE - YS)
        stage.style.width = avail + 'px'
        const xf = yr => PL + (yr - YS) * PPY

        // Ticks
        for (let y = YS + 1; y <= YE; y++) {
            if (y % 2) continue
            const t = document.createElement('div')
            t.className = 'stl-tick'
            t.style.left = xf(y) + 'px'
            t.innerHTML = `<div class="stl-tick-mark"></div><div class="stl-tick-yr">${y}</div>`
            stage.appendChild(t)
        }

        // Nodes
        NODES.forEach((n, i) => {
            const side = i % 2 === 0 ? 'above' : 'below'
            const node = document.createElement('div')
            node.className = 'stl-node'
            node.style.left = xf(n.yr) + 'px'
            node.style.setProperty('--dot-c', n.color)
            const r = hexRgb(n.color)
            const chipsHtml = n.chips.map(c => `<span class="stl-chip" style="color:${n.color};border-color:rgba(${r},.35);background:rgba(${r},.09);">${c}</span>`).join('')
            const prog = n.programs ? `<p class="stl-programs">${n.programs}</p>` : ''
            node.innerHTML = `
                <div class="stl-dot"></div>
                <div class="stl-popup stl-pop-${side}">
                    <div class="stl-card">
                        <div class="stl-card-label">
                            <span class="stl-card-yr">${n.yr}</span>
                            <span class="stl-card-title">${n.label}</span>
                        </div>
                        <div class="stl-card-detail">
                            <p class="stl-detail-era">${n.era}</p>
                            <p class="stl-detail-heading">${n.heading}</p>
                            <p class="stl-detail-body">${n.body}</p>
                            <div class="stl-chips">${chipsHtml}</div>
                            ${prog}
                        </div>
                    </div>
                    <div class="stl-stem"></div>
                </div>`
            node.addEventListener('mouseenter', () => {
                stage.querySelectorAll('.stl-node.expanded').forEach(x => x.classList.remove('expanded'))
                node.classList.add('expanded')
            })
            stage.appendChild(node)
        })

        // Auto-expand first
        stage.querySelector('.stl-node')?.classList.add('expanded')

        // Drag to scroll
        let dn = false, sx, sl
        outer.addEventListener('mousedown', e => { dn = true; sx = e.pageX - outer.offsetLeft; sl = outer.scrollLeft })
        outer.addEventListener('mouseleave', () => { dn = false })
        outer.addEventListener('mouseup', () => { dn = false })
        outer.addEventListener('mousemove', e => {
            if (!dn) return; e.preventDefault()
            outer.scrollLeft = sl - (e.pageX - outer.offsetLeft - sx) * 1.1
        })
    }, [])

    return (
        <V2Layout pageClass="pfc-story-v1">
            {/* Hero */}
            <section className="pfc-page-hero">
                <div className="pfc-page-hero-inner">
                    <p className="v2-section-label">Our Story · Option A - Horizontal Timeline</p>
                    <h1 className="pfc-page-hero-title" style={{ color: 'var(--v2-text)' }}>Two Decades of Precision &amp; Growth</h1>
                    <p className="pfc-page-hero-sub">
                        From a 2,000 sq ft workshop in Auburn to a {stats.facility_size.toLowerCase()} sq ft campus in Lincoln, CA - {new Date().getFullYear() - parseInt(stats.year_founded)} years of fluid control systems, precision machining, and environmental testing.
                    </p>
                </div>
            </section>

            {/* Stats */}
            <section className="v2-stats-bar">
                <div className="v2-stats-inner cols-5">
                    <div className="v2-stat"><span className="v2-stat-num">{stats.year_founded}</span><span className="v2-stat-label">Founded</span></div>
                    <div className="v2-stat"><span className="v2-stat-num">8+</span><span className="v2-stat-label">Product Lines</span></div>
                    <div className="v2-stat"><span className="v2-stat-num">2</span><span className="v2-stat-label">Acquisitions</span></div>
                    <div className="v2-stat"><span className="v2-stat-num">{stats.facility_size}</span><span className="v2-stat-label">Sq Ft Today</span></div>
                    <div className="v2-stat"><span className="v2-stat-num">{stats.cnc_total}</span><span className="v2-stat-label">CNC Machines</span></div>
                </div>
            </section>

            {/* Horizontal Timeline */}
            <section className="stl-section">
                <div className="stl-hint">
                    <p className="v2-section-label">Milestones · 2004 – 2026 · Hover a node to expand · Scroll or drag to explore</p>
                </div>
                <div className="stl-outer" ref={outerRef}>
                    <div className="stl-stage" ref={stageRef} />
                </div>
            </section>

            {/* Program Eras */}
            <section className="stl-eras-section">
                <p className="v2-section-label">Programs Supported</p>
                <h2 className="stl-eras-title" style={{ color: 'rgb(150, 120, 230)' }}>By Era</h2>
                <div className="stl-eras-grid">
                    {ERAS.map((era, i) => (
                        <div className={`stl-era-card${i === ERAS.length - 1 ? ' stl-era-full' : ''}`} key={i}>
                            <div className="stl-era-period">{era.period}</div>
                            <p className="stl-era-desc">{era.desc}</p>
                            <div className="stl-era-tags">
                                {era.tags.map(t => <span className="stl-era-tag" key={t}>{t}</span>)}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </V2Layout>
    )
}

export default OurStoryV1Screen
